import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import authService from "@/services/authService";
import { useToast } from "@/components/ui/use-toast";

const VerifyEmail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { verifySignupOtp, isAuthenticated } = useAuth();

  const [email, setEmail] = useState<string>("");
  const [code, setCode] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [booting, setBooting] = useState(true); // <-- NEW
  const { toast } = useToast();

  // Grab email from state or session
  useEffect(() => {
    const fromState = (location.state as { email?: string; firstName?: string } | null)?.email;
    const fromStorage = sessionStorage.getItem("pendingSignupEmail") || "";
    const picked = (fromState || fromStorage || "").trim().toLowerCase();
    setEmail(picked);

    // If we were redirected straight from login, keep an overlay for a brief tick
    const redirected = (location.state as any)?.redirected === true;
    if (redirected) {
      // end boot overlay on next frame so the user never sees a white flash
      const id = setTimeout(() => setBooting(false), 0);
      return () => clearTimeout(id);
    }
    setBooting(false);
  }, [location.state]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!cooldown) return;
    const t = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || code.length !== 6) return;
    setIsLoading(true);
    try {
      const result = await verifySignupOtp(email, code);
      sessionStorage.removeItem("pendingSignupEmail");

      const firstName =
        (location.state as any)?.firstName ||
        sessionStorage.getItem("pendingFirstName") ||
        "";

      if (firstName) {
        toast({
          title: "Registration successful",
          description: `Welcome aboard, ${firstName}!`,
        });
        sessionStorage.removeItem("pendingFirstName");
      } else {
        toast({
          title: "Email verified",
          description: "Signed in successfully.",
        });
      }

      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Verification failed",
        description: err?.message || "Please check the code and try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email || cooldown) return;
    try {
      await authService.resendSignupOtp(email);
      toast({ title: "Code sent", description: `We sent a new code to ${email}` });
      setCooldown(30);
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Could not resend",
        description: err?.message || "Try again shortly.",
      });
    }
  };

  // If we lost the email context somehow, send back to signup
  if (!email && !booting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Verify Email</CardTitle>
            <CardDescription>Let’s start again—enter your details to receive a code.</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Link to="/signup">
              <Button>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Signup
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-background p-4">
      {booting && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-[60]">
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            Preparing verification…
          </div>
        </div>
      )}

      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center mb-2">
            <Link to="/signup" className="absolute left-6 top-6">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            </Link>
          </div>
          <CardTitle className="text-2xl">Verify Email</CardTitle>
          <CardDescription>
            We sent a 6-digit code to <span className="font-medium">{email}</span>
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleVerify} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="otp">Enter 6-digit Code</Label>
              <Input
                id="otp"
                inputMode="numeric"
                maxLength={6}
                placeholder="● ● ● ● ● ●"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                disabled={isLoading}
                autoFocus
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || code.length !== 6}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                "Verify & Continue"
              )}
            </Button>

            <div className="text-center text-sm text-muted-foreground">
              Didn’t get the code?{" "}
              <button
                type="button"
                className="text-primary hover:underline disabled:opacity-50"
                onClick={handleResend}
                disabled={!!cooldown}
              >
                {cooldown ? `Resend in ${cooldown}s` : "Resend code"}
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmail;
